import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton, EmptyState } from "@/components/lux";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Bot, CheckCircle2, Activity } from "lucide-react";

const SEV = { low: "#22c55e", medium: "#e6c15a", high: "#f97316", critical: "#ef4444" };
const TYPE_LABEL = {
  auth_failure: "Auth failures", authz_violation: "RBAC violations", admin_probe: "Admin probing",
  idor: "IDOR attempts", rate_limited: "Rate limited", financial_reject: "Financial rejects",
  malicious_input: "Malicious input", server_error: "Server errors",
};

export default function AdminSecurityCenter() {
  const { data: ov, loading, reload } = useFetch("/admin/security/overview");
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);

  const sc = ov?.severity_counts || {};
  const recent = ov?.recent || [];
  const byType = ov?.by_type || {};

  const runAnalyze = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/security/analyze");
      setAnalysis(data);
      toast.success(`AI analysis: ${data.ai_status}`);
      reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const resolve = async (id) => {
    try { await api.post(`/admin/security/events/${id}/resolve`); toast.success("Resolved"); reload(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Failed"); }
  };

  const Stat = ({ label, value, color }) => (
    <div className="rounded-xl glass-2 p-4 text-center" data-testid={`sec-stat-${label.toLowerCase()}`}>
      <p className="text-2xl font-bold" style={{ color: color || "#fff" }}>{value ?? 0}</p>
      <p className="text-[11px] uppercase tracking-wide text-[var(--text-dim)]">{label}</p>
    </div>
  );

  const overall = (sc.critical || 0) > 0 ? "critical" : (sc.high || 0) > 0 ? "high" : (sc.medium || 0) > 0 ? "medium" : "ok";

  return (
    <div className="space-y-6" data-testid="admin-security-center">
      <PageTitle title="Security Center" subtitle="AI-assisted detection on top of deterministic auth / RBAC / rate-limit / idempotency. Detection & advisory only — enforcement stays deterministic." />

      <GlassCard className="p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {overall === "ok" ? <ShieldCheck size={20} className="text-emerald-400" /> : <ShieldAlert size={20} style={{ color: SEV[overall] }} />}
            <span className="font-semibold text-white" data-testid="sec-overall">Overall: <b style={{ color: overall === "ok" ? "#22c55e" : SEV[overall] }}>{overall.toUpperCase()}</b></span>
            <span className="text-xs text-[var(--text-dim)]">· last 24h</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--text-dim)]" data-testid="sec-ai-status">AI: {ov?.ai_available ? "configured" : "not configured (heuristic)"}</span>
            <LuxuryButton variant="gold" disabled={busy} onClick={runAnalyze} data-testid="sec-analyze-btn"><Bot size={15} className="mr-1 inline" />{busy ? "Analyzing…" : "Run AI Analysis"}</LuxuryButton>
          </div>
        </div>
        {loading ? <div className="skeleton h-24" /> : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Stat label="Critical" value={sc.critical} color={SEV.critical} />
            <Stat label="High" value={sc.high} color={SEV.high} />
            <Stat label="Medium" value={sc.medium} color={SEV.medium} />
            <Stat label="Low" value={sc.low} color={SEV.low} />
            <Stat label="Open" value={ov?.open_high_critical} color="#fff" />
          </div>
        )}
        {analysis && (
          <div className="mt-4 rounded-xl glass-2 p-4" data-testid="sec-analysis-result">
            <div className="mb-1 flex items-center gap-2 text-sm text-white"><Activity size={15} className="text-[var(--gold)]" /> Analysis ({analysis.ai_status}) · {analysis.event_count} events</div>
            <p className="whitespace-pre-wrap text-xs text-[var(--text-dim)]">{analysis.summary}</p>
          </div>
        )}
      </GlassCard>

      <div className="grid gap-6 lg:grid-cols-3">
        <GlassCard className="p-6">
          <h3 className="mb-3 font-semibold text-white">Attack Categories (24h)</h3>
          {Object.keys(byType).length ? (
            <div className="space-y-2">
              {Object.entries(byType).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm" data-testid={`sec-type-${k}`}>
                  <span className="text-[var(--text-dim)]">{TYPE_LABEL[k] || k}</span><b className="text-white">{v}</b>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[var(--text-dim)]">No flagged activity in the last 24h.</p>}
        </GlassCard>

        <GlassCard className="p-6 lg:col-span-2">
          <h3 className="mb-3 font-semibold text-white">Recent Security Events</h3>
          {recent.length ? (
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm text-white">
                <thead><tr className="text-left text-[var(--text-dim)]"><th className="p-2">Sev</th><th className="p-2">Type</th><th className="p-2">Endpoint</th><th className="p-2">10m</th><th className="p-2">When</th><th className="p-2"></th></tr></thead>
                <tbody>
                  {recent.map((e) => (
                    <tr key={e.event_id} className="border-t border-white/5" data-testid={`sec-event-${e.event_id}`}>
                      <td className="p-2"><span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: (SEV[e.severity] || "#888") + "22", color: SEV[e.severity] }}>{(e.severity || "").toUpperCase()}</span></td>
                      <td className="p-2 text-xs">{e.event_type}</td>
                      <td className="p-2 font-mono text-[11px] text-[var(--text-dim)]">{e.method} {e.endpoint}</td>
                      <td className="p-2 text-xs">{e.count_10m}</td>
                      <td className="p-2 text-[11px] text-[var(--text-dim)]">{(e.created_at || "").slice(11, 19)}</td>
                      <td className="p-2">{!e.resolved && (e.severity === "high" || e.severity === "critical") ? <button onClick={() => resolve(e.event_id)} className="btn3d btn3d-dark rounded-lg px-2 py-1 text-xs" data-testid={`sec-resolve-${e.event_id}`}><CheckCircle2 size={13} /></button> : (e.resolved ? <span className="text-[10px] text-emerald-400">resolved</span> : null)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState icon={ShieldCheck} title="No security events" subtitle="All quiet — deterministic controls active." />}
        </GlassCard>
      </div>
    </div>
  );
}
